import { Controller, type Control, type FieldPath } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { parsePhoneNumber } from "@/shared/lib/phoneNumber";
import { TextInputStyled } from "./TextInput.style";
import TextField from "@mui/material/TextField";
import { type TRules } from "@/shared/types/form.types";
import { inputStyles, type InputVariant } from "./inputStyles";

interface IPhoneInput<FormNames extends Record<string, any>> {
  control: Control<FormNames>;
  name: FieldPath<FormNames>;
  rules?: TRules<FormNames>;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  onCustomChange?: (val: string) => void;
  variant?: InputVariant;
}

function PhoneInput<FormNames extends Record<string, any>>({
  control,
  name,
  label,
  placeholder = "+998 90 123 45 67",
  rules,
  disabled,
  onCustomChange,
  variant = "default",
}: IPhoneInput<FormNames>) {
  const { t } = useTranslation();

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field, fieldState }) => (
        <TextInputStyled>
          {label && (
            <label htmlFor={name}>
              {label}
              <span>
                {label && rules?.required ? (
                  <span className="text-red-500">*</span>
                ) : null}
              </span>
            </label>
          )}
          <TextField
            {...field}
            type="tel"
            placeholder={placeholder}
            size="small"
            disabled={disabled}
            fullWidth
            error={!!fieldState.error}
            onFocus={(e) => {
              // If the field is empty or doesn't start with +998, set it to +998
              const currentValue = e.target.value;
              if (!currentValue || !currentValue.startsWith('+998')) {
                const formatted = parsePhoneNumber('+998', { type: "withPlus" });
                field.onChange(formatted);
                onCustomChange?.(formatted);
              }
            }}
            onChange={(e) => {
              const value = e.target.value;
              // Remove all non-digits except +
              const cleanValue = value.replace(/[^\d+]/g, '');
              
              let phoneValue = cleanValue;
              
              // If empty or just +, set to +998
              if (!cleanValue || cleanValue === '+') {
                phoneValue = '+998';
              }
              // If starts with + but not +998, replace with +998
              else if (cleanValue.startsWith('+') && !cleanValue.startsWith('+998')) {
                phoneValue = '+998' + cleanValue.substring(1);
              }
              // If doesn't start with +, add +998 prefix
              else if (!cleanValue.startsWith('+')) {
                phoneValue = '+998' + cleanValue;
              }
              
              // Remove extra + signs and ensure only one +
              phoneValue = phoneValue.replace(/^\+/, '');
              phoneValue = '+' + phoneValue.replace(/\+/g, '');
              
              // Format the phone number (parsePhoneNumber will handle the 12 digit limit)
              const formatted = parsePhoneNumber(phoneValue, { type: "withPlus" });
              field.onChange(formatted);
              onCustomChange?.(formatted);
            }}
            sx={inputStyles[variant]}
          />
          {fieldState.error && (
            <h6 style={{ 
              color: '#d32f2f', 
              fontSize: '12px', 
              textAlign: 'left', 
              marginTop: '4px',
              marginBottom: 0,
              lineHeight: '14px',
              fontWeight: 400
            }}>
              {fieldState.error.message}
            </h6>
          )}
        </TextInputStyled>
      )}
    />
  );
}

export default PhoneInput;
